import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { AGGREGATE_NAMES, LOAN_REQUEST_COMMANDS, MUTATION_KEYS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { verifyConsorsSubscriptionCancellationStatus } from '../../../LeadSideInfo/ExternalServices/utils/verifyConsorsSubscriptionCancellationStatus';
import { APICommunicator } from '~/api/APICommunicator.ts';
import useLead from '~/features/leadDetails/hooks/useLead';
import useUserInfo from '~/hooks/useUserInfo';
import { queryKeys } from '~/utils/queryKeys';

export const useCancelConsorsSubscription = () => {
    const { t } = useTranslation();
    const user = useUserInfo();
    const queryClient = useQueryClient();
    const { useParams } = getRouteApi('/_app/leads_/details/$leadId');
    const { leadId } = useParams();
    const { data: lead } = useLead(leadId);
    const subscriptionIdentifier = lead?.consors?.subscriptionIdentifier;

    return useMutation({
        mutationFn: async () => {
            return APICommunicator.sendCommand({
                aggregateId: leadId,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: LOAN_REQUEST_COMMANDS.cancelConsorsSubscription,
                payload: {
                    loanRequestId: leadId,
                    subscriptionIdentifier,
                    userId: user?.id,
                },
            });
        },
        mutationKey: [MUTATION_KEYS.CANCEL_CONSORS_SUBSCRIPTION, leadId],
        onError: () => {
            toast.error(t('CREDIT_WORTHINESS.CONSORS_PROCESS.SUBSCRIPTION_CANCELLATION_FAILED'));
        },
        onSuccess: async () => {
            if (!subscriptionIdentifier) {
                return;
            }
            await verifyConsorsSubscriptionCancellationStatus({
                loanRequestId: leadId,
                subscriptionIdentifier,
                t,
            });
            await queryClient.invalidateQueries({ queryKey: queryKeys.get(leadId) });
            await queryClient.invalidateQueries({ queryKey: queryKeys.getAcceptedOffers(leadId) });
            await queryClient.invalidateQueries({ queryKey: queryKeys.getAcceptedOfferCounts(leadId) });
        },
    });
};
